import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SAFE_LEVEL_COLORS, pbLevelIndex, safeConfidence } from '@/data/mockSafeData';
import type { FundamentalCatalogItem, PersonalResponse, PersonalGrowthResponse } from '@/api/types';
import { formatScore, NO_SCORE_LABEL } from '@/utils/formatScore';
import { formatMonthLabel } from '@/utils/formatMonthLabel';
import { FundamentalIcon } from '@/components/common/FundamentalIcon';
import type { FeedbackItem } from '../utils/buildFeedbackItems';

interface FeedbackDetailPopupProps {
  item: FeedbackItem;
  onClose: () => void;
  catalogData: FundamentalCatalogItem[];
  personalData?: PersonalResponse;
  growthData?: PersonalGrowthResponse;
}

/** Escala de la encuesta: 1 = Inicio … 4 = Referente. */
const MAX_SCORE = 4;
const NO_SCORE_COLOR = '#ccc';
/** Mínimo de opiniones para que la distribución por nivel sea informativa. */
const MIN_DISTRIBUTION_SAMPLE = 3;
/** Meses de historial que caben en la mini gráfica de barras. */
const HISTORY_MONTHS = 6;

/** Bandas de color por puntaje, de mayor a menor. */
const SCORE_COLOR_BANDS: ReadonlyArray<{ min: number; color: string }> = [
  { min: 3.5, color: '#20B15A' },
  { min: 2.5, color: '#40B8E9' },
  { min: 1.5, color: '#F08521' },
  { min: 0, color: '#E11F26' },
];

function scoreColor(value: number): string {
  return SCORE_COLOR_BANDS.find(band => value >= band.min)?.color ?? NO_SCORE_COLOR;
}

/**
 * Devuelve el valor crudo cuando es un número utilizable, o null en caso contrario.
 * Sin esta guarda, un puntaje ausente en la respuesta de la API se propagaba como
 * NaN hasta el ancho de la barra y el índice de nivel. El valor crudo alimenta los
 * cálculos (ancho de barra, índice de nivel, altura de barra del historial);
 * `formatScore` se usa por separado, solo para lo que se imprime en pantalla.
 */
function toUsableScore(value: number | undefined | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function FeedbackDetailPopup({
  item,
  onClose,
  catalogData,
  personalData,
  growthData,
}: FeedbackDetailPopupProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const fund = catalogData.find(entry => entry.id === item.fundamentalId);
  const fundScore = personalData?.fundamentalScores?.find(entry => entry.id === item.fundamentalId);
  const attitudeScore = item.kind === 'attitude'
    ? fundScore?.attitudes?.find(entry => entry.id === item.id)
    : undefined;
  const catalogAttitude = item.kind === 'attitude'
    ? fund?.attitudes.find(entry => entry.id === item.id)
    : undefined;

  if (!fund || !fundScore) return null;
  if (item.kind === 'attitude' && !attitudeScore) return null;

  const description = item.kind === 'attitude'
    ? (catalogAttitude?.descriptions?.[lang] ?? catalogAttitude?.description ?? '')
    : (fund.descriptions?.[lang] ?? fund.description ?? '');

  const color = item.color;

  const avgValue = toUsableScore(item.score) ?? 0;
  // Cabecera y barra de progreso rotulan personas, no puntajes: usan responses, no
  // evaluations. Antes ambos leían evaluations y la cabecera decía "12 colegas opinaron"
  // mientras la distribución de abajo, que sí cuenta una entrada por respuesta, sumaba 4.
  const count    = item.responses ?? 0;
  const lvl      = pbLevelIndex(avgValue);
  const conf     = safeConfidence(count);
  const pct      = Math.round((avgValue / MAX_SCORE) * 100);
  const lvlColor = SAFE_LEVEL_COLORS[lvl];

  // Distribution from real aggregate data, at the level opened.
  const distribution = item.kind === 'attitude'
    ? (attitudeScore?.distribution ?? [])
    : (fundScore.distribution ?? []);
  const distTotal = distribution.reduce((total, levelCount) => total + levelCount, 0);
  const distPct   = (levelIndex: number) =>
    distTotal > 0 ? Math.round(((distribution[levelIndex] ?? 0) / distTotal) * 100) : 0;

  // Monthly history — last 6 months from growthData, sorted ascending, at the level opened.
  const hist = [...(growthData?.history ?? [])]
    .sort((first, second) => first.month.localeCompare(second.month))
    .slice(-HISTORY_MONTHS)
    .map(snapshot => ({
      label: formatMonthLabel(snapshot.month, lang),
      avg: toUsableScore(
        item.kind === 'attitude'
          ? snapshot.attitudes.find(entry => entry.attitudeId === item.id)?.average
          : snapshot.fundamentals.find(entry => entry.fundamentalId === item.id)?.average,
      ) ?? 0,
    }));

  // Daily habits scored al nivel abierto: solo los del comportamiento, o los del
  // fundamental completo cuando no hay nivel intermedio.
  const habitScores = item.kind === 'attitude'
    ? (attitudeScore?.dailyHabits ?? [])
    : (fundScore.dailyHabits ?? []);

  return (
    <div
      ref={overlayRef}
      className="modal-overlay modal-overlay--fade"
      onClick={event => { if (event.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
    >
      <div className="fp-popup">
        <button
          className="fp-popup-close"
          onClick={onClose}
          aria-label={t('common.close')}
          type="button"
        >
          <i className="fas fa-times" />
        </button>

        {/* Header */}
        <div className="fp-popup-header">
          <div className="fp-popup-icon" style={{ background: `${color}18`, color }}>
            <FundamentalIcon icon={item.icon} fallbackIcon="fa-layer-group" />
          </div>
          <div className="fp-popup-title-wrap">
            <h2>{item.name}</h2>
            <p>{description}</p>
          </div>
          <div className="fp-popup-score-wrap">
            <span className="fp-popup-score" style={{ color: lvlColor }}>{formatScore(avgValue)}</span>
            <span
              className="fp-popup-score-label"
              style={{ background: `${lvlColor}18`, color: lvlColor }}
            >
              {t(`safe.level${lvl}`)}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="fp-popup-progress">
          <div className="fp-popup-bar-track">
            <div className="fp-popup-bar-fill" style={{ width: `${pct}%`, background: color }} />
          </div>
          <div className="fp-popup-bar-meta">
            <span>{count} {t('safe.popup.peersOpined')} · {conf}</span>
            <span>{formatScore(avgValue)} / {MAX_SCORE}</span>
          </div>
        </div>

        {/* Daily habits at the level opened */}
        {habitScores.length > 0 && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-puzzle-piece" />
              {item.kind === 'attitude' ? t('safe.popup.dailyHabits') : t('safe.popup.subbehaviors')}
            </h3>
            <div className="fp-popup-subbeh">
              {habitScores.map(habitScore => {
                const catalogHabit = fund.dailyHabits.find(entry => entry.id === habitScore.id);
                const habitValue = toUsableScore(habitScore?.score);
                const isScored   = habitValue !== null;
                const barPct     = isScored ? Math.round((habitValue / MAX_SCORE) * 100) : 0;
                const barColor   = isScored ? scoreColor(habitValue) : NO_SCORE_COLOR;
                const levelLabel = isScored ? t(`safe.level${pbLevelIndex(habitValue)}`) : NO_SCORE_LABEL;
                const habitName  = catalogHabit?.names[lang] ?? catalogHabit?.names['en']
                  ?? catalogHabit?.names['es'] ?? habitScore.id;
                return (
                  <div key={habitScore.id} className="fp-popup-subbeh-row">
                    <div className="fp-popup-subbeh-info">
                      <span className="fp-popup-subbeh-name">{habitName}</span>
                      <span className="fp-popup-subbeh-meta">
                        {t('safe.popup.evaluationsCount', { count: habitScore.evaluations ?? 0 })}
                        {' · '}{levelLabel}
                      </span>
                    </div>
                    <div className="fp-popup-subbeh-bar-track">
                      <div
                        className="fp-popup-subbeh-bar-fill"
                        style={{ width: `${barPct}%`, background: barColor }}
                      />
                    </div>
                    <span className="fp-popup-subbeh-score" style={{ color: barColor }}>
                      {formatScore(habitValue)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Monthly history */}
        {hist.length > 0 && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-chart-line" />
              {t('safe.popup.monthlyHistory')}
            </h3>
            <div className="fp-popup-months">
              {hist.map(snapshot => (
                <div key={snapshot.label} className="fp-popup-month">
                  <div className="fp-popup-month-bar-track">
                    <div
                      className="fp-popup-month-bar-fill"
                      style={{ height: snapshot.avg ? `${(snapshot.avg / MAX_SCORE) * 100}%` : '0%', background: color }}
                    />
                  </div>
                  <span className="fp-popup-month-val">
                    {snapshot.avg ? formatScore(snapshot.avg) : NO_SCORE_LABEL}
                  </span>
                  <span className="fp-popup-month-label">{snapshot.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Distribution */}
        {distTotal >= MIN_DISTRIBUTION_SAMPLE && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-chart-bar" />
              {t('safe.popup.distribution')}
            </h3>
            <div className="fp-popup-dist">
              {([1, 2, 3, 4] as const).map(l => (
                <div key={l} className="fp-popup-dist-row">
                  <span className="fp-popup-dist-label" style={{ color: SAFE_LEVEL_COLORS[l - 1] }}>
                    {t(`safe.level${l - 1}`)}
                  </span>
                  <div className="fp-popup-dist-bar-track">
                    <div
                      className="fp-popup-dist-bar-fill"
                      style={{ width: `${distPct(l - 1)}%`, background: SAFE_LEVEL_COLORS[l - 1] }}
                    />
                  </div>
                  <span className="fp-popup-dist-pct">{distPct(l - 1)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Behavior scale — global level descriptions (same for all fundamentals) */}
        <div className="fp-popup-section">
          <h3>
            <i className="fas fa-stairs" />
            {t('safe.popup.behaviorScale')}
          </h3>
          <div className="fp-popup-levels">
            {([0, 1, 2, 3] as const).map(levelIndex => (
              <div
                key={levelIndex}
                className={`fp-popup-level${levelIndex === lvl ? ' current' : ''}`}
              >
                <div
                  className="fp-popup-level-num"
                  style={{ background: SAFE_LEVEL_COLORS[levelIndex] }}
                >
                  {levelIndex + 1}
                </div>
                <div className="fp-popup-level-content">
                  <strong style={{ color: SAFE_LEVEL_COLORS[levelIndex] }}>
                    {t(`safe.level${levelIndex}`)}
                  </strong>
                  <p>{t(`safe.level${levelIndex}desc`)}</p>
                </div>
                {levelIndex === lvl && (
                  <span className="fp-popup-level-you">← {t('safe.popup.you')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
